/** Query-Parameter, über den ein Gutschein-Code per URL übergeben wird (etwa aus Promolinks). */
export const PROMO_QUERY_PARAM = 'coupon';

/**
 * Query-Parameter, mit dem der Redirect eines Promolinks dessen Slug markiert. Bleibt in der URL
 * erhalten und dient dem Tracking sowie der Zuordnung von Käufen zum Promolink.
 */
export const PROMO_LINK_QUERY_PARAM = 'promo';

/** Query-Parameter, die von Promolinks selbst gesetzt werden und nicht frei belegt werden dürfen. */
export const RESERVED_PROMO_PARAMS: readonly string[] = [PROMO_QUERY_PARAM, PROMO_LINK_QUERY_PARAM];

/** Pfad-Präfix der Promo-Kurzlinks (`/p/<slug>`). */
export const PROMO_LINK_PATH = '/p';

const PERCENT_BASE = 100;

/** Minimale Gutschein-Daten, die für die Preisberechnung benötigt werden. */
export interface DiscountCoupon {
    /** Gutschein-Typ. */
    type: 'percentage' | 'value' | 'walk_unlock';
    /** Cent-Betrag (value) oder Prozent (percentage). */
    value?: number | null;
}

/**
 * Berechnet den Endpreis eines Walks nach Anwendung eines Gutscheins.
 * Wird identisch auf Client (Streichpreis-Anzeige) und Server (Checkout) verwendet,
 * damit angezeigter und berechneter Preis nie auseinanderlaufen.
 *
 * @param price  Der Originalpreis in Cent.
 * @param coupon Der anzuwendende Gutschein.
 * @returns Endpreis und Rabattbetrag in Cent.
 */
export const calculateDiscountedPrice = (
    price: number,
    coupon: DiscountCoupon
): {discountAmount: number; finalAmount: number} => {
    let discountAmount = 0;

    if (coupon.type === 'walk_unlock') {
        discountAmount = price;
    } else if (coupon.type === 'value') {
        discountAmount = Math.min(coupon.value ?? 0, price);
    } else {
        discountAmount = Math.round(price * ((coupon.value ?? 0) / PERCENT_BASE));
    }

    return {
        discountAmount,
        finalAmount: Math.max(0, price - discountAmount)
    };
};

/**
 * Liefert die ID einer Payload-Relation, egal ob sie als ID oder populiertes Dokument vorliegt.
 *
 * @param rel Die Relation.
 * @returns Die Dokument-ID.
 */
export const toRelationId = (rel: string | {id: string}): string => (typeof rel === 'string' ? rel : rel.id);

/**
 * Hängt einen Gutschein-Code und optional den Promolink-Marker als Query-Parameter an einen internen Pfad an.
 *
 * @param path      Der Pfad, optional bereits mit Query-String.
 * @param code      Der Gutschein-Code oder null.
 * @param promoLink Der Promolink-Slug oder null.
 * @returns Der Pfad mit `?coupon=…&promo=…` oder unverändert ohne Werte.
 */
export const withPromoCode = (
    path: string,
    code: string | null | undefined,
    promoLink?: string | null
): string => {
    const [base, search = ''] = path.split('?');
    const params = new URLSearchParams(search);

    if (code) params.set(PROMO_QUERY_PARAM, code);
    if (promoLink) params.set(PROMO_LINK_QUERY_PARAM, promoLink);

    const query = params.toString();

    return query ? `${base}?${query}` : base;
};